/**
 * Where a project lives in the browser: `/p/<slug>/...`.
 *
 * main.tsx reads the slug off the address once and starts the project's router
 * with `basename: "/p/<slug>"`, so every in-app link (`to="/experiments/1"`)
 * stays project-relative. Leaving the project (another project, `/`, sign-in)
 * is a full page load, so the query cache never mixes two
 * projects; these helpers are the only places that build such a URL.
 */

const LAST_PROJECT = "workbench:last-project";
const FLASH = "workbench:flash";

/** The project slug a browser path is in, or null outside any project (or
 *  when the slug is not valid percent-encoding). */
export function projectFromPath(pathname: string): string | null {
  const match = /^\/p\/([^/]+)/.exec(pathname);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]!);
  } catch {
    return null;
  }
}

/** `/p/<slug>` plus an in-project path (`/experiments/1?tab=x`). */
export function projectHref(slug: string, path = "/"): string {
  const rest = path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
  return `/p/${encodeURIComponent(slug)}${rest}`;
}

/** Open a project with a fresh page (and so a fresh query cache). `replace`
 *  drops the current entry from history, for redirects; `path` opens a page in it. */
export function openProject(slug: string, { replace = false, path = "/" } = {}) {
  const href = projectHref(slug, path);
  if (replace) window.location.replace(href);
  else window.location.assign(href);
}

export function rememberProject(slug: string) {
  try {
    localStorage.setItem(LAST_PROJECT, slug);
  } catch {
    /* storage blocked: `/` opens your first project instead */
  }
}

export function lastProject(): string | null {
  try {
    return localStorage.getItem(LAST_PROJECT);
  } catch {
    return null;
  }
}

/** Stop reopening a project that is gone or closed to you (only if it is the remembered one). */
export function forgetProject(slug: string) {
  try {
    if (localStorage.getItem(LAST_PROJECT) === slug) localStorage.removeItem(LAST_PROJECT);
  } catch {
    /* storage unavailable */
  }
}

/** A toast for the next page, since leaving a project is a full page load. */
export function flash(message: string) {
  try {
    sessionStorage.setItem(FLASH, message);
  } catch {
    /* storage blocked: the toast is lost, the action still happened */
  }
}

/** Read (and clear) the toast the previous page left. */
export function takeFlash(): string | null {
  try {
    const message = sessionStorage.getItem(FLASH);
    sessionStorage.removeItem(FLASH);
    return message;
  } catch {
    return null;
  }
}

/** Go to a full browser path after sign-in. A project path belongs to another
 *  router, so it is a page load; anything else is an in-app navigation. */
export function goToPath(path: string, navigate: (to: string, options: { replace: boolean }) => void) {
  if (projectFromPath(path)) window.location.replace(path);
  else navigate(path, { replace: true });
}

/** Out of a project you left or deleted: forget it and go to `/`, which opens
 *  another project (or the first-project screen) with `message` as a toast. */
export function leaveProject(slug: string | null, message?: string) {
  if (slug) forgetProject(slug);
  if (message) flash(message);
  window.location.assign("/");
}
